import style from "./style.scss";

import { Fragment } from "preact";
import Link from "../../../components/link";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Ligature from "../../../components/ligature";
import Accordion from "../../../components/accordion";
import VoteItems from "../../../components/vote-items";
import namedRoutes from "../../../settings/routes";

const pageTitle = "2025 - Vote - Klahanie.info";

const Vote = () => (
  <div>
    <Head title={pageTitle} />
    <Hero background="/images/backgrounds/forest-path.jpg">
      <h1>
        <span style="font-size: 0.5em; font-weight: 400; display: block; margin-bottom: 0.25em; text-transform: none;">
          Klahanie{" "}
        </span>
        <Ligature>2025 Voting Info</Ligature>
      </h1>
    </Hero>
    <Editorial>
      <Center>
        <h2 id="how-to-vote">How to Vote</h2>
        <p>
          You should have already received your voting information. If not,
          please see details below. Voting ends March 7<sup>th</sup> at 11:59
          PM.
        </p>
        <p>Jump to section:</p>
        <ul>
          <li>
            <strong>
              <Link href="#online-ballot">Online Ballot</Link>
            </strong>
          </li>
          <li>
            <strong>
              <Link href="#paper-ballot">Paper Ballot</Link>
            </strong>
          </li>
          <li>
            <strong>
              <Link href="#ballot-items">Ballot Items</Link>
            </strong>
          </li>
          <li>
            <strong>
              <Link href="#resources">Resources</Link>
            </strong>
          </li>
        </ul>
      </Center>
      <Center>
        <h2 id="online-ballot">Online Ballot</h2>
        <p>
          If you received an email from <em>Vote HOA Now</em>, use your personal
          registration code on the{" "}
          <Link
            target="_blank"
            rel="noreferrer"
            href="https://klahanie.ivotehoa.com/register"
          >
            community voting site
          </Link>
          . You might need to check your spam folder.
        </p>

        <p>
          If you didn't receive a personal registration code, there are two
          primary options:
          <ul>
            <li>
              <strong>Pick up a code in person:</strong> You can pick up the
              code in person at the Klahanie Association Office. Remember to
              bring your identification so your address and identity can be
              verified. See the{" "}
              <Link
                target="_blank"
                rel="noreferrer"
                href="https://www.klahanie.com/contact"
              >
                office hours
              </Link>{" "}
              page for scheduled hours, however this can be impacted by the
              school's snow schedule in winter.
            </li>
            <li>
              <strong>Request a code via email:</strong> If you cannot make it
              into the office during business hours, you can email a picture of
              your ID to{" "}
              <Link rel="noreferrer" href="mailto:info@klahanie.com">
                info@klahanie.com
              </Link>
              . Once staff has verified your identity and residency, they will
              email you your voting code.
            </li>
          </ul>
        </p>

        <p>
          There is a form on the community voting site's{" "}
          <Link
            target="_blank"
            rel="noreferrer"
            href="https://klahanie.ivotehoa.com/contact"
          >
            contact page
          </Link>
          , however they still require you to go in person or submit your ID via
          email to receive a code so this seems like an extra unnecessary step.
        </p>
      </Center>
      <Center>
        <h2 id="paper-ballot">Paper Ballot</h2>
        <p>
          Fill out a paper ballot or proxy at the Klahanie Association Office or
          request a paper ballot be sent to your home address. All paper ballots
          and proxies must be turned in at the Klahanie Association Office by
          March 7<sup>th</sup> at 4:00 PM. Remember to bring your identification
          so your address and identity can be verified when you arrive. See the{" "}
          <Link
            target="_blank"
            rel="noreferrer"
            href="https://www.klahanie.com/contact"
          >
            office hours
          </Link>{" "}
          page for scheduled hours, however this can be impacted by the school's
          snow schedule in winter.
        </p>
      </Center>
      <Center>
        <h2 id="ballot-items">Ballot Items</h2>
        <p>
          Items highlighted in green are supported by candidates{" "}
          <strong>
            <Link href={namedRoutes.archive2025.jimCline}>Jim Cline</Link>
          </strong>
          ,{" "}
          <strong>
            <Link href={namedRoutes.archive2025.ericaFloden}>Erica Floden</Link>
          </strong>
          , and{" "}
          <strong>
            <Link href={namedRoutes.archive2025.rachelLong}>Rachel Long</Link>
          </strong>
          :
        </p>
        <Accordion
          items={[
            {
              question: "Make a choice",
              answer: (
                <VoteItems
                  items={[
                    {
                      selected: true,
                      text: "Vote on 2 items and for up to 3 candidates (choices will be on the next screens)",
                    },
                    { text: "Submit for quorum purposes only" },
                  ]}
                />
              ),
            },
            {
              question: "2024 Annual Meeting Minutes",
              answer: (
                <Fragment>
                  <p>
                    View page 7 of the attachment for the 2024 Annual Meeting
                    Minutes.{" "}
                    <Link
                      target="_blank"
                      href="/downloads/2024-annual-meeting-minutes.pdf"
                    >
                      View meeting minutes
                    </Link>
                  </p>
                  <VoteItems
                    items={[
                      {
                        text: "Yes, I approve the 2024 Annual Meeting Minutes",
                      },
                      {
                        text: "No, I do not approve the 2024 Annual Meeting Minutes",
                      },
                    ]}
                  />
                </Fragment>
              ),
            },
            {
              question: "Revenue Ruling 70-604 of Excess Income",
              answer: (
                <Fragment>
                  <p>
                    Motion to adopt the Resolution for Revenue Ruling 70-604
                    Election-Excess Income Applied to the Following Year’s
                    Assessment, as required by the Internal Revenue Service
                    (IRS). The purpose of revenue ruling 70-604 is to allow a
                    homeowner’s association that has excess member income in a
                    given tax year to either refund that excess to the members
                    or roll it over to the next tax year to avoid taxation of
                    the “inadvertent” excess member income.{" "}
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      href="https://hoatax.com/guide-revenue-ruling-70-604/"
                    >
                      Learn more
                    </Link>
                  </p>
                  <VoteItems
                    items={[
                      {
                        selected: true,
                        text: "Yes, I approve the Revenue Ruling 70-604 of Excess Income",
                      },
                      {
                        text: "No, I do not approve the Revenue Ruling 70-604 of Excess Income",
                      },
                    ]}
                  />
                </Fragment>
              ),
            },
            {
              question: "Candidates",
              answer: (
                <Fragment>
                  <p>Select up to 3:</p>
                  <VoteItems
                    items={[
                      { text: "Don Stephanian" },
                      {
                        selected: true,
                        text: (
                          <Link href={namedRoutes.archive2025.ericaFloden}>
                            Erica Floden
                          </Link>
                        ),
                      },
                      { text: "Jennifer Shepherd" },
                      {
                        selected: true,
                        text: (
                          <Link href={namedRoutes.archive2025.jimCline}>
                            Jim Cline
                          </Link>
                        ),
                      },
                      { text: "Kim Wheelock" },
                      {
                        selected: true,
                        text: (
                          <Link href={namedRoutes.archive2025.rachelLong}>
                            Rachel Long
                          </Link>
                        ),
                      },
                      { text: "Scott Powell" },
                    ]}
                  />
                  <p>Write in another candidate...</p>
                </Fragment>
              ),
            },
          ]}
        />
        <ul class={style.buttonList}>
          <li>
            <Link
              class="button"
              target="_blank"
              rel="noreferrer"
              href="https://klahanie.ivotehoa.com/register"
            >
              Vote Online
            </Link>
          </li>
          <li>
            <Link
              class="button secondary"
              target="_blank"
              rel="noreferrer"
              href="https://www.klahanie.com/contact"
            >
              Contact HOA Office
            </Link>
          </li>
        </ul>
      </Center>
      <Center>
        <h2 id="resources">Resources</h2>
        <ul>
          <li>
            <strong>
              <Link
                href="https://www.facebook.com/groups/618929933832686/"
                target="_blank"
                rel="noreferrer"
              >
                Klahanie Residents Facebook group
              </Link>
            </strong>{" "}
            - Have discussions on <strong>ALL things</strong> Klahanie.
          </li>
          <li>
            <strong>
              <Link
                href="https://klahanie.ivotehoa.com/register"
                target="_blank"
                rel="noreferrer"
              >
                Vote Online
              </Link>
            </strong>{" "}
            - via Vote HOA Now.
          </li>
          <li>
            <strong>
              <Link
                href="https://www.klahanie.com/"
                target="_blank"
                rel="noreferrer"
              >
                Klahanie HOA website
              </Link>
            </strong>{" "}
            - Homeowners Association.
          </li>
          <li>
            <strong>
              <Link
                href="https://www.klahanie.com/residentportal"
                target="_blank"
                rel="noreferrer"
              >
                Klahanie Resident Portal
              </Link>
            </strong>{" "}
            - HOA documents and other information.
          </li>
          <li>
            <strong>
              <Link
                href="https://www.klahanie.com/overview"
                target="_blank"
                rel="noreferrer"
              >
                View bylaws and related documents
              </Link>
            </strong>
          </li>
          <li>
            <strong>
              <Link href={namedRoutes.archive2025.downloads}>
                Download campaign assets
              </Link>
            </strong>
          </li>
        </ul>
        <div class={style.buttonWrapper}>
          <Link class="button" href="/">
            View More Info
          </Link>
        </div>

        <h5 style="margin-top: 2em;">Check back for more updates</h5>
      </Center>
    </Editorial>
  </div>
);

export default Vote;
